import { useState, useCallback } from "react";

export interface VerifyAuthResponse {
  allowed: boolean;
  email?: string;
}

export interface UseVerifyGoogleAuthReturn {
  loading: boolean;
  allowed: boolean | null;
  email: string | null;
  error: string | null;
  verify: (token: string, verifyUrl: string) => Promise<VerifyAuthResponse | null>;
  reset: () => void;
}

/**
 * Hook to verify Google ID token with backend allowlist
 * 
 * @returns Object with loading state, verification result, and verify function
 */
export function useVerifyGoogleAuth(): UseVerifyGoogleAuthReturn {
  const [loading, setLoading] = useState(false);
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const verify = useCallback(
    async (token: string, verifyUrl: string): Promise<VerifyAuthResponse | null> => {
      if (!token) {
        setError("No token provided");
        return null;
      }

      if (!verifyUrl) {
        setError("Verification URL not configured");
        return null;
      }

      setLoading(true);
      setError(null);
      setAllowed(null);
      setEmail(null);

      try {
        const response = await fetch(verifyUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ idToken: token }),
        });

        if (!response.ok) {
          throw new Error(`Verification failed: ${response.status} ${response.statusText}`);
        }

        const data: VerifyAuthResponse = await response.json();

        setAllowed(data.allowed);
        if (data.allowed && data.email) {
          setEmail(data.email);
        }

        return data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to verify authentication";
        setError(errorMessage);
        setAllowed(false);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const reset = useCallback(() => {
    setLoading(false);
    setAllowed(null);
    setEmail(null);
    setError(null);
  }, []);

  return {
    loading,
    allowed,
    email,
    error,
    verify,
    reset,
  };
}
